import { DOCUMENT_SCOPES } from '../../../generic/qr/qrDocumentResources'

/**
 * What the operator can pick from after a scan, as a tree.
 *
 * Most elements offer two flat levels (their own documents and the article's) and tabs are enough for
 * that. An order does not: it offers its own documents plus every active line, and under each line
 * its fabrication orders — a shape that grows with the order and would turn into dozens of tabs.
 *
 * Kept as a pure module because the interesting decisions are decisions, not rendering: which node
 * opens first, and which nodes can be opened at all.
 */

// Identity of a node for selection and for keying the viewer. The pair is unique by construction:
// it is what the document endpoint is addressed by.
export const nodeKey = (node) => node.resourceType + ':' + node.referenceId

/**
 * Builds the pickable tree.
 *
 * The order's lines hang off its INSTANCE level, not off the answer as a whole: they belong to the
 * order, and the level that represents the order itself is that one. Any other level (an article's
 * drawings, when the type has them) stays a sibling root with no children of its own.
 *
 * @param {Array<object>} sources document levels of the scanned element, as normalised by qrLookupApi
 * @param {Array<object>} orderLines lines of a scanned order, each with its fabricationOrders
 * @returns {Array<object>} nodes with `key` and `children`, children being nodes themselves
 */
export const buildDocumentTree = (sources = [], orderLines = []) => {
  const lines = lineNodes(orderLines)
  // No level of its own to hang from: the structure stands on its own rather than being dropped,
  // because the drawings under it are the reason the label was scanned.
  if (sources.length === 0) return lines

  const owner = sources.find((source) => source.scope === DOCUMENT_SCOPES.INSTANCE) ?? sources[0]

  return sources.map((source) => ({
    ...source,
    key: nodeKey(source),
    children: source === owner ? lines : []
  }))
}

const lineNodes = (orderLines) => orderLines.map((line) => ({
  ...line,
  key: nodeKey(line),
  children: (line.fabricationOrders ?? []).map((fabricationOrder) => ({
    ...fabricationOrder,
    key: nodeKey(fabricationOrder),
    children: []
  }))
}))

/** Every node of the tree, depth first — the order a reader sees them in. */
export const flattenTree = (nodes = []) =>
  nodes.flatMap((node) => [node, ...flattenTree(node.children)])

/**
 * The node to open first: the level the station asked for when it has documents, otherwise the first
 * node that has any, otherwise the first node at all.
 *
 * Never an empty node while a filled one exists: landing on "nothing here" reads as "this element has
 * no documents" when the drawings are one click away.
 */
export const initialNodeKey = (nodes = [], preferredScope = DOCUMENT_SCOPES.REFERENCE) => {
  const all = flattenTree(nodes)
  const preferred = all.find((node) => node.scope === preferredScope && node.documentCount > 0)
  const filled = preferred ?? all.find((node) => node.documentCount > 0)
  return (filled ?? all[0])?.key ?? ''
}

/** A node with no documents cannot be opened: there would be nothing to show. */
export const isOpenable = (node) => Boolean(node) && node.documentCount > 0
